import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const METRICS = [
  { key: "signals",  label: "Signals",       color: "#a78bfa" },
  { key: "trades",   label: "Trades",        color: "#10b981" },
  { key: "users",    label: "New users",     color: "#3b82f6" },
  { key: "llm_cost", label: "LLM cost (USD)", color: "#f59e0b" },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [series, setSeries] = useState({});   // { signals: [...], trades: [...], ... }
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const out = {};
    await Promise.all(METRICS.map(async (m) => {
      try {
        const r = await api.get(`/admin/analytics/timeline?metric=${m.key}&days=${days}`);
        out[m.key] = r.data.series || [];
      } catch { out[m.key] = []; }
    }));
    setSeries(out);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="space-y-5" data-testid="admin-analytics-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter">Analytics</h1>
          <div className="vx-label mt-1">Time-series across all users</div>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-3 py-1 rounded border text-xs font-mono uppercase tracking-widest ${
                      days === d ? "border-[#dc2626] text-[#dc2626] bg-[#dc2626]/10" : "border-[#1f2937] text-[#9ca3af] hover:bg-[#1f2937]/40"
                    }`}
                    data-testid={`range-${d}d`}>
              {d}d
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="text-[#9ca3af] text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {METRICS.map((m) => (
            <section
              key={m.key}
              className="rounded-lg border border-[#1f2937] bg-[#111827] p-4"
              data-testid={`chart-${m.key}`}
            >
              <div className="text-sm font-semibold mb-2" style={{ color: m.color }}>{m.label}</div>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series[m.key] || []}>
                    <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#6b7280" fontSize={10} />
                    <YAxis stroke="#6b7280" fontSize={10} />
                    <Tooltip
                      contentStyle={{ background: "#0a0e1a", border: "1px solid #1f2937", fontSize: 11 }}
                      labelStyle={{ color: "#9ca3af" }}
                    />
                    <Line type="monotone" dataKey="value" stroke={m.color} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[10px] text-[#6b7280] font-mono mt-1">
                {series[m.key]?.length || 0} data points
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
